import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { Music2, Mic2, Users, Star, ArrowRight, Heart, Quote, Facebook, Instagram, Youtube, CheckCircle } from 'lucide-react';
import { defaultTestimonials } from '@/lib/defaults';
import { db } from '@/lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { motion } from 'framer-motion';
import Autoplay from 'embla-carousel-autoplay';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function buildMailtoBook(s) {
  const subject = encodeURIComponent('Singvolve: Booking Enquiry - Singing Lessons');
  const body = encodeURIComponent(`I'd like to book singing lessons.\n\nName - Pupil: \nName - Parent/Carer (If applicable): \nEmail: \nTelephone number: \nAre you available on a Tuesday or Thursday evening between 5-7pm to have a 30 minute lesson at the Bristol Cathedral Choir School (BCCS) in Bristol: \nWhat style of music would you like to try (optional): `);
  return `mailto:${s.adminEmail}?subject=${subject}&body=${body}`;
}

export default function Home() {
  const settings = useSiteSettings();
  const s = settings;
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [activeSlide, setActiveSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState(null);
  const social = settings.socialLinks || {};

  useEffect(() => {
    async function loadTestimonials() {
      try {
        const snap = await getDocs(collection(db, 'testimonials'));
        if (!snap.empty) {
          const firestoreTestimonials = snap.docs
            .map(d => ({ ...d.data(), id: d.id }))
            .filter(t => t.enabled !== false);
          if (firestoreTestimonials.length > 0) {
            setTestimonials(firestoreTestimonials);
          }
        }
      } catch (e) {
        console.log('Using default testimonials');
      }
    }
    loadTestimonials();
  }, []);

  const onSelect = useCallback(() => {
    if (!carouselApi) return;
    setActiveSlide(carouselApi.selectedScrollSnap());
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    onSelect();
    carouselApi.on('select', onSelect);
    return () => { carouselApi.off('select', onSelect); };
  }, [carouselApi, onSelect]);

  return (
    <div data-testid="home-page">
      {/* Social Bar */}
      <div className="bg-[#F5F0F7] border-b border-purple-100" data-testid="home-social-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between">
          <p className="text-xs text-[#5E4B66] font-medium">Follow Singvolve</p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2" data-testid="home-credential-badges">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-[#4B1D5E]/20 bg-white text-xs font-medium text-[#4B1D5E]" data-testid="badge-dbs">
                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                DBS Checked
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-md border border-[#4B1D5E]/20 bg-white text-xs font-medium text-[#4B1D5E]" data-testid="badge-ism">
                Member of ISM
              </span>
            </div>
            <div className="w-px h-5 bg-[#4B1D5E]/15" />
            <div className="flex items-center gap-3" data-testid="home-social-icons">
              <a href={social.facebook || '#'} target={social.facebook ? '_blank' : undefined} rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#4B1D5E]/10 flex items-center justify-center hover:bg-[#4B1D5E] hover:text-white text-[#4B1D5E] transition-all" data-testid="home-social-facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={social.instagram || '#'} target={social.instagram ? '_blank' : undefined} rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#4B1D5E]/10 flex items-center justify-center hover:bg-[#4B1D5E] hover:text-white text-[#4B1D5E] transition-all" data-testid="home-social-instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={social.youtube || '#'} target={social.youtube ? '_blank' : undefined} rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#4B1D5E]/10 flex items-center justify-center hover:bg-[#4B1D5E] hover:text-white text-[#4B1D5E] transition-all" data-testid="home-social-youtube">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">
                Bristol's Singing Teacher
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D1B36] leading-tight mb-6">
                Evolve Your <span className="text-[#4B1D5E]">Voice</span>
              </h1>
              <p className="text-lg text-[#5E4B66] leading-relaxed mb-8 max-w-lg">
                Professional, supportive singing lessons for children and adults in Bristol. Build confidence, discover your voice, and fall in love with singing.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={buildMailtoBook(s)} data-testid="hero-book-cta">
                  <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105">
                    Book Lessons <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                {s.tasterEnabled && (
                  <Link to="/contact" data-testid="hero-taster-cta">
                    <Button variant="outline" className="border-2 border-[#4B1D5E] text-[#4B1D5E] hover:bg-[#F5F0F7] rounded-full px-8 py-6 text-lg font-semibold">
                      Free Taster Session
                    </Button>
                  </Link>
                )}
              </div>
              {s.tasterEnabled && (
                <p className="mt-4 text-sm text-[#8A7E90]">
                  Next taster: {s.tasterDate} at {s.tasterTime}, {s.tasterLocation}
                </p>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {s.images?.hero && (
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={s.images.hero}
                    alt="Singing lesson"
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#4B1D5E]/20 to-transparent" />
                </div>
              )}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#F5F0F7] flex items-center justify-center">
                  <Star className="h-6 w-6 text-[#FFD700]" />
                </div>
                <div>
                  <p className="font-bold text-[#2D1B36]">5.0 Rating</p>
                  <p className="text-xs text-[#8A7E90]">From our students</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#FAFAFA] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }} className="mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#2D1B36] mb-4">Why Singvolve?</h2>
            <p className="text-base sm:text-lg text-[#5E4B66] max-w-2xl">Every voice is unique. Our lessons are designed to help you discover and develop yours in a supportive, encouraging environment.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Mic2, title: 'All Ages Welcome', desc: 'From age 7 to adults. Lessons tailored to your experience level.' },
              { icon: Music2, title: 'Any Genre', desc: 'Pop, musical theatre, classical, folk and more. You choose the music.' },
              { icon: Heart, title: 'Build Confidence', desc: 'A safe, encouraging space to grow your voice and self-belief.' },
              { icon: Users, title: 'Student Portal', desc: 'Track your progress, access notes and homework between lessons.' },
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 h-full">
                  <CardContent className="p-8 flex flex-col items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-[#F5F0F7] flex items-center justify-center">
                      <item.icon className="h-7 w-7 text-[#4B1D5E]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">{item.title}</h3>
                    <p className="text-sm text-[#5E4B66] leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20 lg:py-28 bg-white" data-testid="testimonials-carousel-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }} className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#2D1B36] mb-3">What Our Students Say</h2>
              <p className="text-base text-[#5E4B66]">Hear from students and parents who love their Singvolve experience.</p>
            </div>
            <Link to="/testimonials" className="hidden sm:block text-[#4B1D5E] font-medium text-sm hover:underline" data-testid="home-testimonials-link">
              View all <ArrowRight className="inline h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
            <Carousel
              setApi={setCarouselApi}
              plugins={[Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })]}
              opts={{ loop: true, align: 'start' }}
              className="w-full"
              data-testid="testimonials-carousel"
            >
              <CarouselContent className="-ml-6">
                {testimonials.map((t) => (
                  <CarouselItem key={t.id} className="pl-6 md:basis-1/2" data-testid={`testimonial-slide-${t.id}`}>
                    <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 h-full">
                      <CardContent className="p-8 relative">
                        <Quote className="absolute top-6 right-6 h-8 w-8 text-[#F5F0F7]" strokeWidth={1.5} />
                        <div className="flex gap-1 mb-4">
                          {Array.from({ length: t.rating || 5 }).map((_, j) => (
                            <Star key={j} className="h-4 w-4 fill-[#FFD700] text-[#FFD700]" />
                          ))}
                        </div>
                        <p className="text-[#2D1B36] leading-relaxed mb-6 italic text-base min-h-[80px]">"{t.text}"</p>
                        <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
                          <div className="w-10 h-10 rounded-full bg-[#F5F0F7] flex items-center justify-center text-[#4B1D5E] font-semibold text-sm">
                            {t.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-[#2D1B36] text-sm">{t.name}</p>
                            <p className="text-xs text-[#8A7E90]">{t.role}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex items-center justify-center gap-3 mt-8">
                <CarouselPrevious className="static translate-y-0 h-9 w-9 border-purple-200 text-[#4B1D5E] hover:bg-[#F5F0F7] hover:text-[#4B1D5E]" data-testid="carousel-prev" />
                <div className="flex gap-2" data-testid="carousel-dots">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => carouselApi?.scrollTo(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${activeSlide === i ? 'w-6 bg-[#4B1D5E]' : 'w-2 bg-[#B088BC]/40 hover:bg-[#B088BC]'}`}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>
                <CarouselNext className="static translate-y-0 h-9 w-9 border-purple-200 text-[#4B1D5E] hover:bg-[#F5F0F7] hover:text-[#4B1D5E]" data-testid="carousel-next" />
              </div>
            </Carousel>
          </motion.div>

          <Link to="/testimonials" className="sm:hidden block text-center mt-8 text-[#4B1D5E] font-medium text-sm hover:underline">
            View all testimonials <ArrowRight className="inline h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#4B1D5E] py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">Ready to Start Singing?</h2>
          <p className="text-lg text-white/80 mb-10 max-w-xl mx-auto">
            Book a block of {s.lessonCount} lessons or try a free taster session. Every voice deserves to be heard.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={buildMailtoBook(s)} data-testid="cta-book">
              <Button className="bg-white text-[#4B1D5E] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-lg">
                Book Lessons
              </Button>
            </a>
            <Link to="/contact" data-testid="cta-contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
